import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { explorer } from "../config";
import { ago, isoDate, pad, shortAddr } from "../lib/format";
import { sigilDataUri } from "../lib/sigil";

export function Sigil({ id, size = 48, className = "" }: { id: bigint | number | string; size?: number; className?: string }) {
  const src = useMemo(() => sigilDataUri(id), [id]);
  return <img className={`sigil ${className}`} src={src} width={size} height={size} alt="" />;
}

export function Bar({ value, max, variant }: { value: number; max: number; variant?: "accent" }) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div
      className={`bar ${pct >= 100 ? "complete" : ""} ${variant ?? ""}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
    >
      <div className="fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Stat({ label, value, sub, children }: { label: string; value?: ReactNode; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      {value !== undefined && <div className="value">{value}</div>}
      {sub !== undefined && <div className="sub">{sub}</div>}
      {children}
    </div>
  );
}

/** Re-render periodically so relative times stay fresh. */
function useNow(intervalMs = 30_000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

export function Ago({ ts }: { ts: bigint | number | null | undefined }) {
  const now = useNow();
  if (ts === null || ts === undefined) return <span>—</span>;
  const label = ago(ts, now);
  const date = isoDate(ts);
  return date === "—" ? <span>—</span> : <time dateTime={new Date(Number(ts) * 1000).toISOString()}>{label}</time>;
}

export function Addr({ address, n = 4 }: { address: string | null | undefined; n?: number }) {
  if (!address) return <span className="faint">—</span>;
  const text = shortAddr(address, n);
  return explorer ? (
    <a href={`${explorer}/address/${address}`} target="_blank" rel="noreferrer" title={address}>
      {text}
    </a>
  ) : (
    <span title={address}>{text}</span>
  );
}

export function TxLink({ hash, children }: { hash: string | null | undefined; children?: ReactNode }) {
  if (!hash) return <span className="faint">—</span>;
  const text = children ?? shortAddr(hash, 4);
  return explorer ? (
    <a href={`${explorer}/tx/${hash}`} target="_blank" rel="noreferrer" title={hash}>
      {text}
    </a>
  ) : (
    <span title={hash}>{text}</span>
  );
}

export function LiveDot({ live, label }: { live: boolean; label?: string }) {
  return (
    <span className="row" style={{ "--gap": "7px" } as React.CSSProperties}>
      <span className={`dot ${live ? "live" : ""}`} />
      {label && <span className={live ? "live-text" : "tiny muted"}>{label}</span>}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function AgentHandle({ id, name, size = 20 }: { id: bigint; name: string | null; size?: number }) {
  return (
    <Link to={`/agents/${id}`} className="author agent" style={{ textDecoration: "none" }}>
      <Sigil id={id} size={size} />
      <span className="who">{name ?? `agent ${pad(id)}`}</span>
      <span className="faint">#{id.toString()}</span>
    </Link>
  );
}

export function Author({ isAgent, agentId, agentName, author }: { isAgent: boolean; agentId: bigint | null; agentName: string | null; author: string }) {
  if (isAgent && agentId !== null) return <AgentHandle id={agentId} name={agentName} />;
  return (
    <span className="author">
      <span className="dot" />
      <span>
        <Addr address={author} />
      </span>
    </span>
  );
}

export function SkeletonLines({ lines = 3 }: { lines?: number }) {
  return (
    <div className="stack" style={{ "--gap": "8px" } as React.CSSProperties}>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${90 - i * 17}%` }} />
      ))}
    </div>
  );
}
